import React, { FC, ReactNode, useEffect } from 'react'
import { useParams } from 'react-router'
import {
    Box,
    Container,
    LayoutGrid,
    Paragraph,
    Spinner,
    Flex,
    Text,
    Heading,
    Card,
    Link,
    Button
} from '@asktia/tia-ui'
import { THREAD_LABELS } from 'src/globals'
import CompanyWith from 'src/pages/YourCare/CompanyWith'

import { Link as RouterLink } from 'react-router-dom'

import { HeaderChatButton, HeaderYourCareButton } from 'src/components/Blocks'
import { useCarePlanIntervention } from 'src/hooks/useCarePlanIntervention'
import { CenteredSpinner } from 'src/pages/YourCare/HealthRecord'
import { Intervention, Partner } from 'src/types'
import { PageHeader } from 'src/components/PageHeader'
import Linkify from 'src/components/Linkify'
import { useAmpli, useNavigation, useUserInfo } from 'src/hooks'
import { Body } from 'src/components/Blocks/Body'
import { BackButton } from 'src/components/Blocks/BackButton'
import { useAppointment } from 'src/hooks/useAppointment'
import { EncounterCard } from 'src/pages/AppointmentHistory/components/EncounterCard'
import { NavBarHeading } from 'src/components/Blocks/NavBarHeading'

import ReactMarkdown from 'react-markdown'
import { IMarkdown } from 'src/components/Markdown'

const DetailSection: FC<React.PropsWithChildren<{ title: ReactNode }>> = ({
    title,
    children
}) => {
    if (!children) {
        return null
    }

    return (
        <Card
            sx={{
                mb: 4,
                py: 4
            }}
        >
            {title}
            <Paragraph variant="h3" sx={{ mt: 2, mb: 0 }}>
                {children}
            </Paragraph>
        </Card>
    )
}

const PartnerTitle: FC<React.PropsWithChildren<{ partner: Partner }>> = ({
    partner
}) => {
    if (partner.type === 'integrated') {
        return (
            <Flex sx={{ alignItems: 'center', gap: '8px' }}>
                <Text sx={{ color: 'supportText' }} variant="eyebrow">
                    in partnership with
                </Text>
                <CompanyWith partner={partner}></CompanyWith>
            </Flex>
        )
    } else {
        return <></>
    }
}

const PartnerCta: FC<React.PropsWithChildren<{ partner: Partner }>> = ({
    partner
}) => {
    const { ampli } = useAmpli()
    const { navigateOutsideRouter } = useNavigation()

    return (
        <Button
            as="a"
            fullWidth
            sx={{ mt: 2, mb: 4 }}
            onClick={() => {
                // Track the click on the follow-up CTA button
                ampli.partnerCtaClicked({
                    name: partner.name,
                    type: partner.type
                })
                navigateOutsideRouter(
                    // @ts-ignore
                    partner.followUpUrl,
                    partner?.name || '_blank'
                )
            }}
        >
            <Flex
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                }}
            >
                {partner.followUpCta} with{' '}
                <CompanyWith fill="white" partner={partner}></CompanyWith>
            </Flex>
        </Button>
    )
}

type PartnerMarkdownProps = Omit<IMarkdown, 'text'> & { partner: Partner }

/**
 * PartnerMarkdown component renders the patient education content
 * from the partner, with special handling for links.
 * It uses ReactMarkdown to parse the markdown text and render it.
 * The links are styled and track clicks using Ampli.
 */
const PartnerMarkdown = (props: PartnerMarkdownProps) => {
    const { ampli } = useAmpli()
    const { partner, sx, anchorSx, ...settings } = props

    /**
     * The text to be rendered is taken from the partner's patientEducation field
     */
    const text = partner.patientEducation || ''

    /**
     * The care type name is derived from the partner's displayName or name.
     * This is used to create a unique identifier for the link.
     * It is also used for tracking clicks on the link.
     */
    const careTypeName = partner.displayName || partner.name || ''

    const markdownProps: IMarkdown = {
        ...settings,
        text,
        components: {
            p: ({ children }) => <>{children}</>,
            a: ({ node: _node, ...nodeProps }) => {
                const targetUrl = nodeProps.href || partner.followUpUrl || ''

                /**
                 * The target for the link is derived from the care type name.
                 * It is converted to lowercase and spaces are replaced with hyphens.
                 * This is used to create a unique identifier for the link.
                 * This is useful for tracking clicks on the link.
                 * It is also used to create a target for the link.
                 */
                const linkTarget = careTypeName.trim().replace(/\s+/g, '-')

                const handleLinkClick = () => {
                    ampli.nextStepsLinkClicked({
                        targetUrl,
                        careTypeName
                    })
                }

                return (
                    <Link
                        target={linkTarget}
                        {...nodeProps}
                        {...markdownProps}
                        sx={anchorSx}
                        onClick={handleLinkClick}
                    >
                        {nodeProps.children}
                    </Link>
                )
            }
        }
    }

    return (
        <Paragraph {...markdownProps} sx={sx}>
            <ReactMarkdown {...markdownProps}>
                {markdownProps.text}
            </ReactMarkdown>
        </Paragraph>
    )
}

const PartnerSection: FC<React.PropsWithChildren<{ partner: Partner }>> = ({
    partner
}) => {
    return (
        <>
            {partner.patientEducation && (
                <DetailSection title={<PartnerTitle partner={partner} />}>
                    <PartnerMarkdown partner={partner} />
                </DetailSection>
            )}
            {partner.followUpCta && partner.followUpUrl && (
                <PartnerCta partner={partner} />
            )}
        </>
    )
}

const DetailInfo = (props: {
    intervention: Intervention
    appointmentId?: string
}) => {
    const { carePlanIntervention } = props.intervention

    const { appointment, isLoading: isLoadingAppointment } = useAppointment(
        props.appointmentId
    )
    const { user } = useUserInfo()
    const { ampli } = useAmpli()

    const interventionDetailsReady = props.appointmentId
        ? !isLoadingAppointment
        : !props.appointmentId

    return (
        <Box sx={{ mt: [5, 6] }}>
            {appointment && (
                <>
                    <Heading h2 sx={{ mb: 1 }}>
                        From
                    </Heading>
                    <EncounterCard
                        variant="with-photo"
                        appointment={appointment}
                        sx={{ mb: 4 }}
                    />
                </>
            )}

            {interventionDetailsReady &&
                (carePlanIntervention.patientEducation ||
                    carePlanIntervention.prescriptionDirections) && (
                    <>
                        <Heading h2 sx={{ mb: 1 }}>
                            Details
                        </Heading>
                        <DetailSection
                            title={
                                <Text
                                    sx={{ color: 'supportText' }}
                                    variant="eyebrow"
                                >
                                    {props?.intervention?.originActor
                                        ?.displayName
                                        ? `${props.intervention.originActor.displayName}:`
                                        : ''}
                                </Text>
                            }
                        >
                            <Linkify
                                sx={{
                                    fontFamily: 'body',
                                    fontSize: 3,
                                    fontWeight: 400,
                                    lineHeight: '125%'
                                }}
                                linkSx={{
                                    fontSize: 3,
                                    fontWeight: '400 !important'
                                }}
                            >
                                {carePlanIntervention.patientEducation ||
                                    carePlanIntervention.prescriptionDirections}
                            </Linkify>
                        </DetailSection>

                        {props.intervention.partner && (
                            <>
                                <Heading h2 sx={{ mb: 1 }}>
                                    Next Steps
                                </Heading>
                                <PartnerSection
                                    partner={props.intervention.partner}
                                ></PartnerSection>
                            </>
                        )}

                        <Paragraph sx={{ mb: 4, fontSize: 0 }}>
                            Have questions about your care plan?{' '}
                            <Link
                                as={RouterLink}
                                onClick={() => {
                                    ampli.questionAboutCarePlanDetailsClicked({
                                        intervention: carePlanIntervention.uuid,
                                        userID:
                                            `${user?.id}` ||
                                            'somehow didnt have user id. This should not happen'
                                    })
                                }}
                                // @ts-ignore: TS doesn't understand this is a RouterLink now
                                to={`/chat/new?label=${THREAD_LABELS.followUp}&subject=Follow up care&message=I have a question about my Care Plan`}
                                sx={{ fontSize: 'inherit' }}
                            >
                                please message your Care Coordinator
                            </Link>
                        </Paragraph>
                    </>
                )}
        </Box>
    )
}

export const InterventionDetail = () => {
    const { user, isLoading: userIsLoading } = useUserInfo()
    const { ampli } = useAmpli()
    // Assume: won't route here without intervention uuid
    const { carePlanInterventionUuid = '' } = useParams<{
        carePlanInterventionUuid: string
    }>()

    const { isLoading: isLoadingIntervention, data: intervention } =
        useCarePlanIntervention(carePlanInterventionUuid)

    useEffect(() => {
        if (userIsLoading || isLoadingIntervention || !user || !intervention) {
            return
        }

        ampli.carePlanDetailsViewed({
            careTypeName: intervention?.carePlanIntervention.name,
            intervention: carePlanInterventionUuid,
            userID: user.id.toString()
        })
    }, [userIsLoading, isLoadingIntervention, user, intervention])

    return (
        <Body>
            {isLoadingIntervention && <Spinner />}
            {!isLoadingIntervention && (
                <>
                    <PageHeader
                        desktopActions={
                            <>
                                <HeaderYourCareButton />
                                <HeaderChatButton />
                            </>
                        }
                        mobileBack={<BackButton variant="white" />}
                    />
                    <Container>
                        <NavBarHeading>
                            {intervention?.carePlanIntervention.name}
                        </NavBarHeading>

                        <LayoutGrid columns="one">
                            {isLoadingIntervention && <CenteredSpinner />}
                            {intervention && (
                                <DetailInfo
                                    intervention={intervention}
                                    appointmentId={
                                        intervention?.originAppointment?.id
                                    }
                                />
                            )}
                        </LayoutGrid>
                    </Container>
                </>
            )}
        </Body>
    )
}
